<template>
  <div class="app-container">
    <!-- 顶部筛选 -->
    <el-card shadow="never" class="tk-toolbar-card">
      <div class="tk-toolbar">
        <el-select v-model="statusFilter" class="tk-filter" placeholder="筛选状态" clearable @change="onFilterChange">
          <el-option label="待处理" :value="0" />
          <el-option label="处理中" :value="1" />
          <el-option label="已解决" :value="2" />
          <el-option label="已关闭" :value="3" />
        </el-select>
        <el-select v-model="categoryFilter" class="tk-filter" placeholder="筛选分类" clearable @change="onFilterChange">
          <el-option label="访客留言" value="offline_message" />
        </el-select>
      </div>
    </el-card>

    <!-- 工单列表 -->
    <el-table :data="tickets" v-loading="loading" border style="width:100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
      <el-table-column prop="customerName" label="客户" width="100" />
      <el-table-column label="联系方式" width="170">
        <template slot-scope="scope">
          {{ scope.row.contactValue ? (contactTypeLabel(scope.row.contactType) + ': ' + scope.row.contactValue) : '-' }}
        </template>
      </el-table-column>
      <el-table-column label="优先级" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="priorityTag(scope.row.priority).type" size="small">{{ priorityTag(scope.row.priority).text }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="statusTag(scope.row.status).type" size="small">{{ statusTag(scope.row.status).text }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="建立时间" width="160">
        <template slot-scope="scope">{{ formatTime(scope.row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="230">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openDetail(scope.row.id)">查看</el-button>
          <el-button v-if="scope.row.status === 0" type="text" size="small" @click="openAssignModal(scope.row.id)">分配</el-button>
          <el-button v-if="scope.row.status < 2" type="text" size="small" @click="handleResolve(scope.row.id)">解决</el-button>
          <el-button v-if="scope.row.status < 3" type="text" size="small" style="color:#F56C6C" @click="handleClose(scope.row.id)">关闭</el-button>
        </template>
      </el-table-column>
      <template slot="empty">暂无工单</template>
    </el-table>
    <el-pagination
      class="tk-pagination"
      layout="total, prev, pager, next"
      :current-page="page"
      :page-size="10"
      :total="total"
      @current-change="onPageChange"
    />

    <!-- 分配工单 -->
    <el-dialog title="分配工单" :visible.sync="assignModalOpen" width="480px">
      <el-select v-model="selectedAgentId" class="full-width" placeholder="选择客服">
        <el-option v-for="a in agents" :key="a.id" :label="a.nickname + ' (' + a.username + ')'" :value="a.id" />
      </el-select>
      <div slot="footer" class="dialog-footer">
        <el-button @click="assignModalOpen = false">取消</el-button>
        <el-button type="primary" :disabled="!selectedAgentId" @click="handleAssign">确定</el-button>
      </div>
    </el-dialog>

    <!-- 工单详情 -->
    <el-dialog title="工单详情" :visible.sync="detailOpen" width="640px">
      <div v-loading="detailLoading">
        <div v-if="detailTicket">
          <el-row v-for="(item, i) in detailRows" :key="i" class="tk-detail-row">
            <el-col :span="12" class="tk-detail-cell">
              <span class="tk-detail-label">{{ item.label }}</span>
              <span class="tk-detail-value">
                <el-tag v-if="item.key === 'priority'" :type="priorityTag(detailTicket[item.key]).type" size="small">{{ priorityTag(detailTicket[item.key]).text }}</el-tag>
                <el-tag v-else-if="item.key === 'status'" :type="statusTag(detailTicket[item.key]).type" size="small">{{ statusTag(detailTicket[item.key]).text }}</el-tag>
                <template v-else-if="item.key === 'createTime'">{{ formatTime(detailTicket[item.key], true) }}</template>
                <template v-else-if="item.key === 'contact'">{{ detailTicket.contactValue ? contactTypeLabel(detailTicket.contactType) + ': ' + detailTicket.contactValue : '-' }}</template>
                <template v-else>{{ detailTicket[item.key] || '-' }}</template>
              </span>
            </el-col>
          </el-row>
          <div class="tk-detail-row">
            <span class="tk-detail-label">内容</span>
            <div class="tk-detail-content">
              <div v-for="(line, idx) in contentLines(detailTicket.content)" :key="idx">
                <div v-if="proofImageOf(line)">
                  凭证截图：<br />
                  <a :href="proofImageOf(line).src" target="_blank" rel="noopener noreferrer">
                    <img :src="proofImageOf(line).src" alt="凭证截图" class="tk-proof-img" />
                  </a>
                </div>
                <div v-else>{{ line }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="detailOpen = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listTickets, getTicket, assignTicket, resolveTicket, closeTicket } from '@/api/customerService/ticket'
import { listAgents } from '@/api/customerService/admin'

export default {
  name: 'CustomerServiceTicketList',
  data() {
    return {
      tickets: [],
      total: 0,
      page: 1,
      pageSize: 10,
      statusFilter: undefined,
      categoryFilter: undefined,
      loading: false,
      assignModalOpen: false,
      assignTicketId: null,
      agents: [],
      selectedAgentId: null,
      detailOpen: false,
      detailTicket: null,
      detailLoading: false,
      detailRows: [
        { key: 'id', label: 'ID' },
        { key: 'title', label: '标题' },
        { key: 'customerName', label: '客户' },
        { key: 'contact', label: '联系方式' },
        { key: 'priority', label: '优先级' },
        { key: 'status', label: '状态' },
        { key: 'category', label: '分类' },
        { key: 'createTime', label: '建立时间' }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      listTickets({
        status: this.statusFilter,
        category: this.categoryFilter,
        pageNum: this.page,
        pageSize: this.pageSize
      }).then(res => {
        if (res.code === 0) {
          this.tickets = res.result.list || []
          const rawTotal = res.result && res.result.total
          this.total = Number(rawTotal) > 0 ? Number(rawTotal) : 0
        }
      }).finally(() => { this.loading = false })
    },
    onFilterChange() {
      this.page = 1
      this.loadData()
    },
    onPageChange(p) {
      this.page = p
      this.loadData()
    },
    openAssignModal(ticketId) {
      this.assignTicketId = ticketId
      this.selectedAgentId = null
      listAgents({ pageSize: 200 }).then(res => {
        if (res.code === 0) this.agents = res.result.list || []
      })
      this.assignModalOpen = true
    },
    handleAssign() {
      if (!this.assignTicketId || !this.selectedAgentId) return
      assignTicket(this.assignTicketId, this.selectedAgentId).then(res => {
        if (res.code === 0) {
          this.assignModalOpen = false
          this.$message.success('已分配')
          this.loadData()
        }
      })
    },
    openDetail(id) {
      this.detailOpen = true
      this.detailLoading = true
      getTicket(id).then(res => {
        if (res.code === 0) this.detailTicket = res.result
      }).finally(() => { this.detailLoading = false })
    },
    handleResolve(id) {
      resolveTicket(id).then(res => {
        if (res.code === 0) {
          this.$message.success('已解决')
          this.loadData()
        }
      })
    },
    handleClose(id) {
      this.$confirm('确定关闭该工单？', '提示', { type: 'warning' }).then(() => {
        closeTicket(id).then(res => {
          if (res.code === 0) {
            this.$message.success('已关闭')
            this.loadData()
          }
        })
      }).catch(() => {})
    },
    priorityTag(v) {
      const map = {
        0: { type: 'info', text: '普通' },
        1: { type: 'warning', text: '紧急' },
        2: { type: 'danger', text: '非常紧急' }
      }
      return map[v] || map[0]
    },
    statusTag(v) {
      const map = {
        0: { type: 'primary', text: '待处理' },
        1: { type: 'warning', text: '处理中' },
        2: { type: 'success', text: '已解决' },
        3: { type: 'info', text: '已关闭' }
      }
      return map[v] || { type: 'info', text: '未知' }
    },
    contactTypeLabel(v) {
      const map = { phone: '手机', wechat: '微信', email: 'Email', qq: 'QQ', other: '其他' }
      return v ? (map[v] || v) : '-'
    },
    formatTime(v, full) {
      if (!v) return '-'
      const s = String(v).replace('T', ' ').substring(0, full ? 19 : 16)
      return s || '-'
    },
    contentLines(content) {
      return (content || '-').split('\n')
    },
    proofImageOf(line) {
      const m = String(line || '').match(/凭证截图[：:]\s*((?:https?:\/\/|\/uploads\/)\S+)/)
      if (!m) return null
      return { src: m[1].replace(/^https?:\/\/[^/]+/, '') }
    }
  }
}
</script>

<style scoped lang="scss">
.tk-toolbar-card { margin-bottom: 16px; }
.tk-toolbar-card ::v-deep  .el-card__body { padding: 14px 16px; }
.tk-toolbar { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
.tk-filter { width: 200px; }
.tk-pagination { margin-top: 16px; text-align: right; }
.full-width { width: 100%; }
.tk-detail-row { margin-bottom: 10px; }
.tk-detail-cell { display: flex; align-items: baseline; }
.tk-detail-label { color: #99a2b3; width: 90px; flex-shrink: 0; }
.tk-detail-value { }
.tk-detail-content { white-space: pre-wrap; word-break: break-word; margin-top: 6px; }
.tk-proof-img { max-width: 300px; margin-top: 4px; border: 1px solid #eee; border-radius: 4px; }
</style>